import {
    replyWithAttachmentLastModifiedText,
    accessedOnText,
    authoredByText,
    modifiedByText,
} from './attachButtonMenuItemBuilderUtils.locstring.json';
import loc, { format } from 'owa-localize';
import { getRTL } from 'office-ui-fabric-react/lib/Utilities';
import { logUsage } from 'owa-analytics';
import { AttachmentFile, AttachmentFileType } from 'owa-attachment-file-types';
import { AttachmentConstants } from 'owa-attachment-model-store/lib/utils/attachmentConstants';
import { lazyCreateCloudFileFromAttachmentData } from 'owa-attachment-suggestions';
import { ClientAttachmentId } from 'owa-client-ids';
import { owaDate, OwaDate, formatUserDateTime } from 'owa-datetime';
import { ComposeViewState } from 'owa-mail-compose-store';
import { lazyCreateAttachments } from 'owa-mail-compose-actions';
import { ReplyWithAttachmentSuggestion, ReplyWithEndpointType } from 'owa-mail-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import { formatPastRelativeDateTime } from 'owa-datetime';
import { observableNow } from 'owa-observable-datetime';

const TRUNCATION_SEPARATOR: string = '...';
const MAX_FILENAME_LENGTH: number = 35;

const MAX_BREADCRUMBS_LENGTH: number = 50;

const LTR_PATH_SEPARATOR = ' > ';
const RTL_PATH_SEPARATOR = ' < ';

export async function onAttachmentClicked(
    suggestion: ReplyWithAttachmentSuggestion,
    suggestionIndex: number,
    composeViewState: ComposeViewState
): Promise<void> {
    if (suggestion.Endpoint === ReplyWithEndpointType.MRUClassic) {
        const attachmentId: string = suggestion.Id;

        const attachmentFile: AttachmentFile = {
            fileType: AttachmentFileType.AttachmentItemSuggestion,
            name: suggestion.FileName,
            size: parseInt(suggestion.FileSize),
            attachmentItemId: attachmentId,
            type: AttachmentConstants.attachmentTypes.ITEM_ID_ATTACHMENT_TYPE,
        };

        const createAttachments = await lazyCreateAttachments.import();
        createAttachments(
            [attachmentFile],
            composeViewState,
            {
                isInline: false,
                isHiddenInline: false,
                shouldShare: false,
            },
            null /* processInlineImageBeforeUploadFunc */,
            null /* attachmentInfoBarAddHandler */,
            null /* attachmentInfoBarRemoveHandler */,
            (attachmentId: ClientAttachmentId) =>
                onAttachmentUploaded(attachmentId.Id, suggestion, suggestionIndex)
        );
    } else {
        const createCloudFileFromAttachmentData = await lazyCreateCloudFileFromAttachmentData.import();
        const createAttachments = await lazyCreateAttachments.import();
        await createCloudFileFromAttachmentData(suggestion, cloudFile => {
            createAttachments(
                [cloudFile] /* files */,
                composeViewState,
                {
                    isInline: false,
                    isHiddenInline: false,
                    shouldShare: true,
                },
                null /* processInlineImageBeforeUploadFunc */,
                null /* attachmentInfoBarAddHandler */,
                null /* attachmentInfoBarRemoveHandler */,
                (attachmentId: ClientAttachmentId) =>
                    onAttachmentUploaded(attachmentId.Id, suggestion, suggestionIndex)
            );
        });
    }
}

export async function onAttachmentUploaded(
    generatedAttachmentId: string,
    suggestion: ReplyWithAttachmentSuggestion,
    suggestionIndex: number
): Promise<void> {
    logUsage('AttachmentSuggestionClicked', [
        suggestion.Endpoint,
        suggestion.Id,
        suggestionIndex,
        suggestion.FileSourceType,
        suggestion.FileExtension,
        generatedAttachmentId,
    ]);
}

export function getAttachmentSuggestionMetadata(suggestion: ReplyWithAttachmentSuggestion): string {
    if (suggestion.Endpoint === ReplyWithEndpointType.MRUClassic) {
        // the UX flights below do not apply to classic suggestions
        return getBreadcrumbedFileOrEmailPath(suggestion);
    } else if (isFeatureEnabled('doc-attachment-suggestedAttsUX1')) {
        const { Author: authorDisplayName } = suggestion;
        return format(loc(authoredByText), authorDisplayName);
    } else if (isFeatureEnabled('doc-attachment-suggestedAttsUX2')) {
        const { DateAccessed: dateAccessed } = suggestion;

        if (!dateAccessed) {
            trace.error(
                `[AttachButtonMenuItemBuilder] Access date is not available for suggestion "${
                    suggestion.Id
                }"`
            );
        }

        const date: OwaDate = owaDate('UTC', dateAccessed);
        const dateAccessedText = formatPastRelativeDateTime(date, observableNow());

        return format(loc(accessedOnText), dateAccessedText);
    } else if (isFeatureEnabled('doc-attachment-suggestedAttsUX3')) {
        const { ModifiedBy: modifiedByDisplayName, DateModified: dateModified } = suggestion;

        const date: OwaDate = owaDate('UTC', dateModified);
        const dateModifiedText = formatPastRelativeDateTime(date, observableNow());

        return format(loc(modifiedByText), dateModifiedText, modifiedByDisplayName);
    } else if (isFeatureEnabled('doc-attachment-suggestedAttsUX4')) {
        return getBreadcrumbedFileOrEmailPath(suggestion, true /* middleTruncate */);
    } else {
        return getBreadcrumbedFileOrEmailPath(suggestion);
    }
}

/**
 * For cloudy attachments, we are parsing a sharepoint or onedrive URL.
 * For classic attachments, we are parsing an email folder path ('\Inbox\Address to me' for example)
 */
function getBreadcrumbedFileOrEmailPath(
    suggestion: ReplyWithAttachmentSuggestion,
    middleTruncate: boolean = false
): string {
    let breadcrumbedFileOrEmailPath: string = null;

    // Protect all logic with try/catch since we're parsing data that may not exist in the format we expect.
    try {
        if (suggestion.Endpoint === ReplyWithEndpointType.MRUClassic) {
            // Just display the name of the email containing the attachment.
            breadcrumbedFileOrEmailPath = suggestion.SourceTitle;
        } else {
            const pathBreadcrumbs = getBreadcrumbsForFile(suggestion);
            breadcrumbedFileOrEmailPath = middleTruncate
                ? createMiddleTruncatedBreadcrumbsString(pathBreadcrumbs)
                : createBreadcrumbString(pathBreadcrumbs);
        }
    } catch (e) {
        trace.info(
            '[breadcrumbedFileOrEmailPath] Failed to parse attachment data, message: ' + e.message
        );
    } finally {
        return breadcrumbedFileOrEmailPath;
    }
}

function createBreadcrumbString(breadcrumbs: string[]) {
    const isRTL: boolean = getRTL();

    if (isRTL) {
        return breadcrumbs.reverse().join(RTL_PATH_SEPARATOR);
    } else {
        return breadcrumbs.join(LTR_PATH_SEPARATOR);
    }
}

function createMiddleTruncatedBreadcrumbsString(breadcrumbs: string[]) {
    const breadcrumbsString = createBreadcrumbString(breadcrumbs);
    return middleTruncateString(breadcrumbsString, MAX_BREADCRUMBS_LENGTH);
}

function getBreadcrumbsForFile(suggestion: ReplyWithAttachmentSuggestion): string[] | null {
    // Protect all logic with try/catch since we're parsing data that may not exist in the format we expect.
    try {
        if (suggestion.Endpoint === ReplyWithEndpointType.MRUClassic) {
            // Just display the name of the email containing the attachment.
            return null;
        } else {
            // To parse URL for cloudy attachment, create temporary anchor tag.
            const tempAnchorTag: HTMLAnchorElement = document.createElement('a');
            tempAnchorTag.href = suggestion.Url;

            // Hostname should be something like sharepoint.microsoft.com
            // Pathname should be something like /documents/file.docx
            // Splitting the pathname using / as the delimiter, we can ignore the first and last tokens,
            // as the file path does not include the name of the file itself.
            const hostname = decodeURIComponent(tempAnchorTag.hostname);

            // We ignore the first and last element of the result. As first element is empty
            // and last element is the name of the file which we don't want part of breadcrumbs
            const [, ...pathBreadcrumbs] = decodeURIComponent(tempAnchorTag.pathname).split('/');
            pathBreadcrumbs.pop();

            return [hostname, ...pathBreadcrumbs];
        }
    } catch (e) {
        trace.info(
            '[breadcrumbedFileOrEmailPath] Failed to parse attachment data, message: ' + e.message
        );
        return [];
    }
}

function getTooltipMetadataString(suggestion: ReplyWithAttachmentSuggestion): string[] {
    const date: OwaDate = owaDate('UTC', suggestion.DateModified);
    const lastModifiedDate: string = format(
        loc(replyWithAttachmentLastModifiedText),
        formatUserDateTime(date)
    );

    if (isFeatureEnabled('doc-attachment-suggestedAttsUX1')) {
        return [getAttachmentSuggestionMetadata(suggestion), lastModifiedDate];
    } else if (isFeatureEnabled('doc-attachment-suggestedAttsUX2')) {
        return [getAttachmentSuggestionMetadata(suggestion), lastModifiedDate];
    } else if (isFeatureEnabled('doc-attachment-suggestedAttsUX3')) {
        return [getAttachmentSuggestionMetadata(suggestion)];
    } else {
        // We don't handle the doc-attachment-suggestedAttsUX4 as its breadcrumbs
        // and we already add that so we just need to add the last modified date
        return [lastModifiedDate];
    }
}

export function getTooltipForAttachmentSuggestion(
    suggestion: ReplyWithAttachmentSuggestion
): string {
    let tooltipString: string = null;

    // Protect with try/catch since we're parsing data that may not exist in the format we expect.
    try {
        const tooltipMetadata = getTooltipMetadataString(suggestion);

        tooltipString = [
            suggestion.FileName,
            getBreadcrumbedFileOrEmailPath(suggestion),
            ...tooltipMetadata,
        ].join('\n');
    } catch (e) {
        trace.info(
            '[getTooltipForAttachmentSuggestion] Failed to parse attachment data, message: ' +
                e.message
        );
    } finally {
        return tooltipString;
    }
}

export function fixFileName(suggestion: ReplyWithAttachmentSuggestion): string {
    // If file name contains file extension, remove it. We'll add it separately.
    const fileExtension: string = '.' + suggestion.FileExtension;
    if (suggestion.FileName.indexOf(fileExtension) >= 0) {
        return suggestion.FileName.replace(fileExtension, '');
    } else {
        return suggestion.FileName;
    }
}

export function middleTruncateFileName(fileName: string) {
    // Do not truncate the string if it contains 35 characters or fewer.
    return middleTruncateString(fileName, MAX_FILENAME_LENGTH);
}

function middleTruncateString(str: string, maxLength: number) {
    if (str.length <= maxLength) {
        return str;
    } else {
        const charsToShow: number = maxLength - TRUNCATION_SEPARATOR.length;
        const frontChars: number = Math.ceil(charsToShow / 2);
        const backChars: number = Math.floor(charsToShow / 2);
        return (
            str.substr(0, frontChars) + TRUNCATION_SEPARATOR + str.substr(str.length - backChars)
        );
    }
}

export function getFileExtension(suggestion: ReplyWithAttachmentSuggestion) {
    return '.' + suggestion.FileExtension;
}
